/* eslint-disable complexity */
/**
 * Audit Session Detail Page
 *
 * Xbox-style redesigned page displaying detailed information about a specific audit session
 * with enhanced hero section, integrated overview, and tabbed navigation for audit items and reports.
 */

import type { Community } from '@communities/community/domain/Community';
import { AudioPlayerProvider } from '@core/components';
import { HeroBackground } from '@core/components/HeroBackground';
import { CompleteAuditDialog } from '@internal-audit/components/CompleteAuditDialog';
import type { FlexibleAuditSession, GraphQLCommunity } from '@internal-audit/domain/graphql-types';
import { useGetAuditSession } from '@internal-audit/hooks/useGetAuditSession';
import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AuditCommunityNotFound } from './components/AuditCommunityNotFound';
import { AuditSessionDetailContent } from './components/AuditSessionDetailContent';
import { AuditSessionDetailLoadingState } from './components/AuditSessionDetailLoadingState';
import { AuditSessionErrorDisplay } from './components/AuditSessionErrorDisplay';
import { AuditSessionNotFound } from './components/AuditSessionNotFound';
import { useCompleteAuditDialog } from './components/useCompleteAuditDialog';

// Main Component
export function AuditSessionDetailPage() {
  // Scroll to top when page loads for accessibility and standard web practice
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();
  const { communityId, sessionId } = useParams<{ communityId: string; sessionId: string }>();

  // 4-Query Architecture: Page level only fetches session metadata for header
  // Other components will fetch their own data to avoid prop drilling
  const { session, community, loading, error } = useGetAuditSession(communityId, sessionId);

  // Complete audit dialog management
  const { isCompleteDialogOpen, handleCompleteAudit, handleCompleteAuditConfirm, handleCompleteDialogClose } = useCompleteAuditDialog({ session });

  // Handle delete audit session action
  const handleDeleteAuditSession = useCallback(() => {
    // Navigate back to audits list after successful deletion
    navigate(`/communities/${communityId}/audits`);
  }, [navigate, communityId]);

  // Early returns for different states
  if (loading) {
    return (
      <AuditSessionDetailLoadingState
        communityId={communityId || ''}
        sessionId={sessionId || ''}
        onCompleteAudit={handleCompleteAudit}
        onDeleteAuditSession={handleDeleteAuditSession}
      />
    );
  }

  if (error && !loading) {
    return (
      <AuditSessionErrorDisplay
        error={error as { correlationId?: string; message?: string; userMessage?: string }}
        onClose={() => navigate(-1)}
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (!session) {
    return <AuditSessionNotFound onBackClick={() => navigate(-1)} />;
  }

  if (!community) {
    return <AuditCommunityNotFound />;
  }

  return (
    <AudioPlayerProvider>
      <HeroBackground imageUrl={(community as Community)?.superHeroArtUrl} />
      <AuditSessionDetailContent
        community={community as unknown as GraphQLCommunity}
        communityId={communityId || ''}
        session={session as unknown as FlexibleAuditSession}
        sessionId={sessionId || ''}
        onCompleteAudit={handleCompleteAudit}
        onDeleteAuditSession={handleDeleteAuditSession}
      />

      {/* Complete Audit Dialog */}
      <CompleteAuditDialog
        auditSession={session as unknown as FlexibleAuditSession}
        isOpen={isCompleteDialogOpen}
        onClose={handleCompleteDialogClose}
        onConfirm={handleCompleteAuditConfirm}
      />
    </AudioPlayerProvider>
  );
}
